import { useRef } from 'react';
import { useEmberRamp } from '../hooks/useEmberRamp';

interface Ember {
  left: string;
  size: number;
  duration: number;
  delay: number;
}

const EMBERS: Ember[] = [
  { left: '30%', size: 5, duration: 5.5, delay: 0 },
  { left: '36%', size: 3, duration: 4.4, delay: 0.5 },
  { left: '42%', size: 6, duration: 6.8, delay: 1.4 },
  { left: '47%', size: 4, duration: 5.1, delay: 2.1 },
  { left: '52%', size: 5, duration: 6.1, delay: 2.9 },
  { left: '57%', size: 3, duration: 4.9, delay: 0.9 },
  { left: '63%', size: 6, duration: 5.8, delay: 1.9 },
  { left: '68%', size: 4, duration: 4.6, delay: 3.4 },
  { left: '74%', size: 5, duration: 6.4, delay: 0.2 },
  { left: '40%', size: 4, duration: 5.3, delay: 4.1 },
  { left: '55%', size: 3, duration: 4.7, delay: 3.7 },
  { left: '60%', size: 5, duration: 5.9, delay: 2.5 },
  { left: '33%', size: 3, duration: 5.2, delay: 0.7 },
  { left: '38%', size: 5, duration: 6.2, delay: 2.4 },
  { left: '44%', size: 4, duration: 4.8, delay: 3.1 },
  { left: '49%', size: 6, duration: 5.6, delay: 1.1 },
  { left: '64%', size: 3, duration: 4.5, delay: 4.4 },
  { left: '70%', size: 5, duration: 6.6, delay: 0.3 },
  { left: '35%', size: 4, duration: 5.4, delay: 2.6 },
  { left: '50%', size: 3, duration: 4.3, delay: 1.6 },
  { left: '58%', size: 5, duration: 6.0, delay: 3.9 },
  { left: '66%', size: 4, duration: 5.0, delay: 0.1 },
  { left: '72%', size: 6, duration: 4.9, delay: 2.2 },
  { left: '45%', size: 3, duration: 6.3, delay: 3.3 },
];

export function Hero() {
  const artRef = useRef<HTMLDivElement>(null);
  useEmberRamp(artRef, EMBERS.length);

  return (
    <section id="top" className="relative overflow-hidden px-6 pb-[90px] pt-[150px]">
      <div
        className="pointer-events-none absolute -right-[120px] -top-[160px] h-[620px] w-[620px] animate-[nfs-pulse_9s_ease-in-out_infinite] rounded-full blur-[20px]"
        style={{
          background: 'radial-gradient(circle, var(--glow) 0%, transparent 68%)',
        }}
      />

      <div className="mx-auto grid max-w-[1200px] grid-cols-1 items-center gap-10 min-[900px]:grid-cols-[1.15fr_0.85fr] min-[900px]:gap-14">
        <div>
          <div
            data-reveal
            className="mb-7 inline-flex items-center gap-2.5 rounded-full border border-[var(--line)] py-1.5 pl-2 pr-3.5 text-[12.5px] tracking-[0.06em] text-dim"
          >
            <span className="h-[7px] w-[7px] animate-[nfs-pulse_2.4s_ease-in-out_infinite] rounded-full bg-accent shadow-[0_0_10px_#d07718]" />
            SOFTWARE, REPORTING &amp; AUTOMATION, BUILT AND MAINTAINED
          </div>

          <h1
            data-reveal
            className="m-0 mb-6 text-balance font-display text-[clamp(38px,5.4vw,70px)] font-semibold leading-[1.03] tracking-[-0.028em]"
          >
            <span className="relative inline-block text-accent">
              Stop
              <span className="absolute bottom-[0.06em] left-0 right-0 h-[5px] rounded-[3px] bg-accent opacity-[0.28]" />
            </span>{' '}
            paying for
            <br />
            bad processes.
          </h1>

          <p
            data-reveal
            className="m-0 mb-9 max-w-[600px] text-pretty text-[clamp(16px,1.35vw,18.5px)] leading-[1.65] text-dim"
          >
            Northforge Solutions builds and maintains automation for businesses buried in
            spreadsheets, duplicate data entry, and disconnected systems — so your team spends
            time on the work that matters, not the paperwork around it.
          </p>

          <div data-reveal className="flex flex-wrap items-center gap-3.5">
            <a
              href="#contact"
              className="relative inline-flex items-center gap-2.5 overflow-hidden rounded-full bg-accent px-7 py-[15px] text-base font-medium text-white shadow-[0_14px_34px_-14px_rgba(208,119,24,0.85)] transition-colors hover:bg-accent-cta-hover"
            >
              Start with a free trial
              <span className="text-[17px] leading-none">→</span>
              <span className="absolute bottom-0 top-0 w-[60px] animate-[nfs-sweep_3.6s_ease-in-out_infinite] bg-[linear-gradient(90deg,transparent,rgba(255,255,255,.32),transparent)]" />
            </a>
            <a
              href="#how"
              className="rounded-full border border-[var(--line)] px-6 py-[15px] text-base text-ink transition-colors hover:border-accent"
            >
              See how it works
            </a>
          </div>

          <p data-reveal className="mt-[26px] text-[13.5px] text-dim">
            One real process, automated. You see the impact before we talk terms.
          </p>
        </div>

        <div
          ref={artRef}
          className="relative order-first mx-auto aspect-square w-full max-w-[340px] min-[900px]:order-none min-[900px]:mx-0 min-[900px]:w-auto min-[900px]:max-w-none"
        >
          <div
            className="absolute left-1/2 top-1/2 h-[52%] w-[52%] -translate-x-1/2 -translate-y-1/2 animate-[nfs-pulse_6s_ease-in-out_infinite] rounded-full"
            style={{ background: 'radial-gradient(circle, var(--glow), transparent 70%)' }}
          />
          <div className="absolute left-1/2 top-1/2 aspect-square w-[58%] -translate-x-1/2 -translate-y-1/2">
            <img
              src="/images/NFS_Logo_0.1_light.png"
              alt="Northforge Solutions logo"
              className="absolute inset-0 h-full w-full object-contain transition-opacity duration-[450ms]"
              style={{ opacity: 'var(--logo-light)' }}
            />
            <img
              src="/images/NFS_Logo_0.1.png"
              alt=""
              aria-hidden="true"
              className="absolute inset-0 h-full w-full object-contain transition-opacity duration-[450ms]"
              style={{ opacity: 'var(--logo-dark)' }}
            />
          </div>
          {EMBERS.map((ember, i) => (
            <span
              key={i}
              data-ember={i}
              className="absolute bottom-[15%] rounded-full"
              style={{
                left: ember.left,
                width: ember.size,
                height: ember.size,
                background: 'var(--ember-color)',
                boxShadow: '0 0 6px var(--ember-glow)',
                animation: `nfs-ember ${ember.duration}s ease-out infinite ${ember.delay}s`,
              }}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
